/** Integration (PMS) queries — tenant-scoped. */

import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { queryKeys } from '../query-keys';
import { useApiClient } from '../react/ApiClientProvider';
import {
  type Integration,
  type IntegrationCapabilities,
  integrationCapabilitiesSchema,
  integrationSchema,
} from '../responses';
import { unwrap } from './_helpers';

/** `GET /integrations/:integrationId` — the safe (no-credentials) integration view. */
export function useIntegration(
  tenantId: string,
  integrationId: string,
): UseQueryResult<Integration> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.integration(tenantId, integrationId),
    queryFn: async () => {
      const result = await client.GET('/api/v1/tenants/{tenantId}/integrations/{integrationId}', {
        params: { path: { tenantId, integrationId } },
        parseAs: 'json',
      });
      return unwrap(result, integrationSchema);
    },
    enabled: integrationId.length > 0,
  });
}

/** `GET /integrations/:integrationId/capabilities` — the provider capability matrix. */
export function useIntegrationCapabilities(
  tenantId: string,
  integrationId: string,
): UseQueryResult<IntegrationCapabilities> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.integrationCapabilities(tenantId, integrationId),
    queryFn: async () => {
      const result = await client.GET(
        '/api/v1/tenants/{tenantId}/integrations/{integrationId}/capabilities',
        { params: { path: { tenantId, integrationId } }, parseAs: 'json' },
      );
      return unwrap(result, integrationCapabilitiesSchema);
    },
    enabled: integrationId.length > 0,
  });
}
